import { useState } from 'react';
import { useQuery, useMutation } from 'urql';
import { Q_CONVERSATIONS, Q_CONVERSATION, M_REPLY_CONVERSATION } from '@/lib/queries';
import { useUi } from '@/stores/ui.store';

function timeAgo(iso?: string) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function InboxRoute() {
  const { toast } = useUi();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [body, setBody] = useState('');

  const [list, refetchList] = useQuery({ query: Q_CONVERSATIONS, variables: { unreadOnly } });
  const [detail, refetchDetail] = useQuery({
    query: Q_CONVERSATION,
    variables: { id: selectedId },
    pause: !selectedId,
  });
  const [, reply] = useMutation(M_REPLY_CONVERSATION);

  const conversations: any[] = list.data?.conversations?.data ?? [];
  const convo = detail.data?.conversation;

  const send = async () => {
    if (!selectedId || !body.trim()) return;
    const r = await reply({ conversationId: selectedId, body });
    if (r.error) { toast(r.error.message, 'error'); return; }
    setBody('');
    refetchDetail({ requestPolicy: 'network-only' });
    refetchList({ requestPolicy: 'network-only' });
    toast('Reply sent');
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* List pane */}
      <div className="w-80 border-r border-stone-200 flex flex-col">
        <div className="p-4 border-b border-stone-200">
          <h1 className="text-lg font-semibold text-stone-900">Inbox</h1>
          <label className="flex items-center gap-2 mt-2 text-xs text-stone-500">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
            Unread only
          </label>
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.fetching ? (
            <p className="p-4 text-sm text-stone-500">Loading…</p>
          ) : conversations.length === 0 ? (
            <p className="p-4 text-sm text-stone-400">No conversations.</p>
          ) : (
            conversations.map((c) => (
              <button key={c.id} onClick={() => setSelectedId(c.id)}
                className={`w-full text-left px-4 py-3 border-b border-stone-100 hover:bg-stone-50 ${selectedId === c.id ? 'bg-violet-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-stone-900 truncate flex items-center gap-2">
                    {c.unread && <span className="w-2 h-2 rounded-full bg-violet-600 shrink-0" />}
                    {c.participantHandle}
                  </span>
                  <span className="text-[10px] text-stone-400">{timeAgo(c.lastMessageAt)}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">{c.platform}</span>
                  <span className="text-[10px] text-stone-400">{c.kind}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Detail pane */}
      <div className="flex-1 flex flex-col">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">
            Select a conversation to view messages.
          </div>
        ) : detail.fetching ? (
          <div className="flex-1 flex items-center justify-center text-stone-500 text-sm">Loading…</div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-stone-200">
              <p className="font-semibold text-stone-900">{convo?.participantHandle}</p>
              <p className="text-xs text-stone-500">{convo?.platform} · {convo?.kind}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3 bg-stone-50">
              {(convo?.messages ?? []).map((m: any) => {
                const out = m.direction === 'outbound';
                return (
                  <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-md rounded-2xl px-4 py-2 text-sm ${out ? 'bg-violet-600 text-white' : 'bg-white border border-stone-200 text-stone-800'}`}>
                      {!out && <p className="text-[10px] text-stone-400 mb-0.5">{m.authorHandle}</p>}
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${out ? 'text-violet-200' : 'text-stone-400'}`}>{timeAgo(m.createdAt)} ago</p>
                    </div>
                  </div>
                );
              })}
              {(convo?.messages ?? []).length === 0 && (
                <p className="text-center text-sm text-stone-400">No messages.</p>
              )}
            </div>
            <div className="p-4 border-t border-stone-200 flex gap-2">
              <input value={body} onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type a reply…"
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <button onClick={send} disabled={!body.trim()}
                className="px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-violet-300 text-white rounded-lg text-sm font-medium">Send</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
